import { MuiThemeProvider, AppBar } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React from "react";
import Grid from "@material-ui/core/Grid";
import { chain, chunk } from "lodash";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import PieChart from "../PieChart";
import BarChart from "../BarChart";
import GroupedBarChart from "../GroupedBarChart";
import BarChartWithTrendLine from "../BarChartWithTrendLine";
import themeDashboard from "../../shared/themeDashboard";
import { consts } from "../../../common/consts";
import api from "../../../store/api";
import {
  IReasonForDelayData,
  ITransportByWardData,
  ITopFiveDestinationsData,
  ITransportByDayOfWeek,
  ITransportTimeByDayData,
  ITransFersPerformedData,
} from "../../../common/types";
import debounce from "lodash/debounce";
import eventNames from "../../../store/signalr/eventNames";
import { addTaskForEvent, removeTaskForEvent } from "../../../store/signalr/tasksForEvents";
import { IDashboardCombinedDTO, ITransferStats, IUserType } from "../types/dashboardTypes";
import { hubMessageHandler } from "../helpers/dashboardHelpers";
import TabPanel from "../helpers/TabPanel";
import IAuthState from "../../../interfaces/IAuthState";
import KPIWidgetPT from "./KPIWidgetPT";

interface PatientTransportDashboardWidgetProps {
  startDate: string;
  endDate: string;
  selectedLocations: string[];
  authStore: IAuthState;
  userRoles: IUserType;
}

function a11yProps(index: any) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

const NoData = () => {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
      <h2>No data found !</h2>
    </div>
  );
};

const LoadingData = () => {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
      <h2>Loading data... </h2>
    </div>
  );
};

const componentTaskId = "patientTransportDashboardWidget";

const PatientTransportDashboardWidget: React.FC<PatientTransportDashboardWidgetProps> = ({
  startDate,
  endDate,
  selectedLocations,
  authStore,
  userRoles,
}) => {
  const startDateRef = React.useRef(startDate);
  const endDateRef = React.useRef(endDate);
  const selectedLocationsRef = React.useRef(selectedLocations);

  const [totalTransfers, setTotalTransfers] = React.useState(0);
  const [avgTransferPerDay, setAvgTransferPerDay] = React.useState(0);
  const [transferPerDayYellowAlert, setTransferPerDayYellowAlert] = React.useState<number | null>(null);
  const [transferPerDayRedAlert, setTransferPerDayRedAlert] = React.useState<number | null>(null);
  const [transferPerHour, setTransferPerHour] = React.useState(0);
  const [transferPerHourYellowAlert, setTransferPerHourYellowAlert] = React.useState<number | null>(null);
  const [transferPerHourRedAlert, setTransferPerHourRedAlert] = React.useState<number | null>(null);
  const [transferDistance, setTransferDistance] = React.useState(0);
  const [transferDistanceYellowAlert, setTransferDistanceYellowAlert] = React.useState<number | null>(null);
  const [transferDistanceRedAlert, setTransferDistanceRedAlert] = React.useState<number | null>(null);
  const [percentMultiLoad, setPercentMultiLoad] = React.useState(0);
  const [percentMultiLoadYellowAlert, setPercentMultiLoadYellowAlert] = React.useState<number | null>(null);
  const [percentMultiLoadRedAlert, setPercentMultiLoadRedAlert] = React.useState<number | null>(null);

  const [reasonForDelayData, setReasonForDelayData] = React.useState<IReasonForDelayData[]>([]);
  const [transportByWardData, setTransportByWardData] = React.useState<ITransportByWardData[]>([]);
  const [topFiveDestinationsData, setTopFiveDestinationsData] = React.useState<ITopFiveDestinationsData[]>([]);
  const [transportByDayOfWeek, setTransportByDayOfWeek] = React.useState<ITransportByDayOfWeek[]>([]);
  const [transFersPerformedData, setTransFersPerformedData] = React.useState<ITransFersPerformedData[]>([]);
  const [transportTimeByDayData, setTransportTimeByDayData] = React.useState<ITransportTimeByDayData[] | any[]>([]);
  const [transfersPerformedLoading, setTransfersPerformedLoading] = React.useState<boolean>(true);
  const [reasonForDelayLoading, setReasonForDelayLoading] = React.useState<boolean>(true);
  const [transportByWardLoading, setTransportByWardLoading] = React.useState<boolean>(true);
  const [topFiveDestinationsLoading, setTopFiveDestinationsLoading] = React.useState<boolean>(true);
  const [transportTimeByDayWeekLoading, setTransportTimeByDayWeekLoading] = React.useState<boolean>(true);
  const [transportByDayOfWeekLoading, setTransportByDayOfWeekLoading] = React.useState<boolean>(true);
  const [barChartLoading, setBarChartLoading] = React.useState<boolean>(true);
  const [barChartData, setBarChartData] = React.useState([]);
  const [barChartTabIndex, setBarChartTabIndex] = React.useState(0);
  const [transferByAttendingDoctor, setTransferByAttendingDoctor] = React.useState<any[]>([]);
  const [transferByAttendingDoctorLoading, setTransferByAttendingDoctorLoading] = React.useState<boolean>(true);

  const classes = useStyles();

  const whenNewTransferDo = (transferDetails: any) => {
    if (
      !selectedLocationsRef ||
      selectedLocationsRef.current.length == 0 ||
      selectedLocationsRef.current.filter((l) => l === transferDetails.LocationId).length > 0
    ) {
      hubMessageHandler(startDateRef.current, endDateRef.current, selectedLocationsRef.current, debounceRefreshCharts);
    }
  };

  const whenTransferUpdatedDo = (transferDetails: any) => {
    if (
      !selectedLocationsRef ||
      selectedLocationsRef.current.length == 0 ||
      !transferDetails.PreviousLocationId ||
      selectedLocationsRef.current.filter((l) => l === transferDetails.PreviousLocationId).length > 0 ||
      !transferDetails.NewLocationId ||
      selectedLocationsRef.current.filter((l) => l === transferDetails.NewLocationId).length > 0
    ) {
      hubMessageHandler(startDateRef.current, endDateRef.current, selectedLocationsRef.current, debounceRefreshCharts);
    }
  };

  const whenTransferScheduleUpdatedDo = (transferDetails: any) => {
    if (
      !selectedLocationsRef ||
      selectedLocationsRef.current.length == 0 ||
      !transferDetails.PreviousLocationId ||
      selectedLocationsRef.current.filter((l) => l === transferDetails.PreviousLocationId).length > 0 ||
      !transferDetails.NewLocationId ||
      selectedLocationsRef.current.filter((l) => l === transferDetails.NewLocationId).length > 0
    ) {
      hubMessageHandler(startDateRef.current, endDateRef.current, selectedLocationsRef.current, debounceRefreshCharts);
    }
  };

  const fetchBarChatData = async (reqData: any, barIndex: number) => {
      setBarChartLoading(true);

    try {
      if (barIndex == 0) {
        const resp = await api.getTransferPerServiceDayTotalStat("", reqData);
        setBarChartData(resp.model);
      } else if (barIndex == 1) {
        const resp = await api.getTransferPerServiceHourTotalStat("", reqData);
        setBarChartData(resp.model);
      } else if (barIndex == 2) {
        const resp = await api.getTransferPerServiceDayStat("", reqData);
        setBarChartData(resp.model);
      } else if (barIndex == 3) {
        const resp = await api.getTransferPerServiceHourStat("", reqData);
        setBarChartData(resp.model);
      } else if (barIndex == 4) {
        const resp = await api.getTransferPickupDelayedStat("", reqData);
        setBarChartData(resp.model);
      } else if (barIndex == 5) {
        const resp = await api.getTransferDelayedStat("", reqData);
        setBarChartData(resp.model);
      } else if (barIndex == 6) {
        const resp = await api.getTransferMultiLoadStat("", reqData);
        setBarChartData(resp.model);
      } else if (barIndex == 7) {
        const resp = await api.getTransferDistanceStat("", reqData);
        setBarChartData(resp.model);
      }
    } catch {
      setBarChartData([]);
    }
    setBarChartLoading(false);
  };

  const handleBarChartTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    const reqData = {
      locationId: selectedLocations,
      scheduleStartDate: startDate,
      scheduleEndDate: endDate,
    };

    fetchBarChatData(reqData, newValue);

    setBarChartTabIndex(newValue);
  };

  React.useEffect(() => {
    startDateRef.current = startDate;
    endDateRef.current = endDate;
    selectedLocationsRef.current = selectedLocations;
  });

  React.useEffect(() => {
    addTaskForEvent(eventNames.newTransfer, componentTaskId, whenNewTransferDo);
    addTaskForEvent(eventNames.transferUpdated, componentTaskId, whenTransferUpdatedDo);
    addTaskForEvent(eventNames.transferScheduleUpdated, componentTaskId, whenTransferScheduleUpdatedDo);
    return () => {
      removeTaskForEvent(eventNames.newTransfer, componentTaskId);
      removeTaskForEvent(eventNames.transferUpdated, componentTaskId);
      removeTaskForEvent(eventNames.transferScheduleUpdated, componentTaskId);
    };
  }, [whenNewTransferDo, whenTransferUpdatedDo, whenTransferScheduleUpdatedDo]);

  React.useEffect(() => {
    const reqData = {
      locationId: selectedLocations,
      scheduleStartDate: startDate,
      scheduleEndDate: endDate,
    };
    refreshCharts(reqData);
  }, [startDate, endDate, selectedLocations]);

  const debounceRefreshCharts = React.useMemo(
    () =>
      debounce(async (reqData: any): Promise<void> => {
        await refreshCharts(reqData);
      }, 2000),
    []
  );

  const refreshTransferTotalStat = (dashboardResponse: ITransferStats): void => {
    const {
      recordCount,
      recordCountDailyAllHospitalsBW,
      recordCountPerHourAllHospitalsBW,
      avgKMPerTransfer,
      percentOfMultiload,
      transferPerDayYellowAlert,
      transferPerDayRedAlert,
      transferPerHourYellowAlert,
      transferPerHourRedAlert,
      transferDistanceYellowAlert,
      transferDistanceRedAlert,
      multiLoadTripsYellowAlert,
      multiLoadTripsRedAlert,
    } = dashboardResponse;
    setAvgTransferPerDay(parseFloat(recordCountDailyAllHospitalsBW ? recordCountDailyAllHospitalsBW.toFixed(1) : "0"));
    setTransferPerDayYellowAlert(transferPerDayYellowAlert);
    setTransferPerDayRedAlert(transferPerDayRedAlert);
    setTotalTransfers(recordCount);

    setTransferPerHour(parseFloat(recordCountPerHourAllHospitalsBW ? recordCountPerHourAllHospitalsBW.toFixed(1) : "0"));
    setTransferPerHourYellowAlert(transferPerHourYellowAlert);
    setTransferPerHourRedAlert(transferPerHourRedAlert);

    setTransferDistance(parseFloat(avgKMPerTransfer ? avgKMPerTransfer.toFixed(1) : "0"));
    setTransferDistanceYellowAlert(transferDistanceYellowAlert);
    setTransferDistanceRedAlert(transferDistanceRedAlert);

    setPercentMultiLoad(parseFloat(percentOfMultiload ? percentOfMultiload.toFixed(1) : "0"));
    setPercentMultiLoadYellowAlert(multiLoadTripsYellowAlert);
    setPercentMultiLoadRedAlert(multiLoadTripsRedAlert);
  };

  const refreshTransportTimesByDayOfAWeekStats = (dashboardTimesByDayOfAWeekStatsResponse: any): void => {
    setTransportTimeByDayData(
      chunk(dashboardTimesByDayOfAWeekStatsResponse, dashboardTimesByDayOfAWeekStatsResponse.length / 7).map((item: any[]) => {
        return {
          ...chain(item).keyBy("description").mapValues("recordCount").value(),
          Weekday: item[0].name,
        };
      })
    );
    setTransportTimeByDayWeekLoading(false);
  };

  const refreshTransportByDayOfAWeekStats = (dashboardListResponse: any): void => {
    setTransportByDayOfWeek(
      dashboardListResponse.map(({ name, recordCount }: any) => ({
        id: name,
        label: name,
        value: recordCount,
      }))
    );
    setTransportByDayOfWeekLoading(false);
  };

  const refreshTopFiveDestinationsStats = (dashboardTopDestinationsResponse: any): void => {
    setTopFiveDestinationsData(
      dashboardTopDestinationsResponse.map(({ name, recordCount }: any) => ({
        id: name,
        label: name,
        value: recordCount,
      }))
    );
    setTopFiveDestinationsLoading(false);
  };

  const refreshTransfersPerformedStats = (dashboardTransferResponse: any): void => {
    const data = dashboardTransferResponse.map(({ name, recordCount, pickUpDelayed, transferDelayed, aborted }: ITransFersPerformedData) => ({
      date: name,
      "Total transfers": recordCount,
      "Pickup time delayed": pickUpDelayed,
      "Transfers delayed": transferDelayed,
      Aborted: aborted,
    }));
    setTransFersPerformedData(data);
    setTransfersPerformedLoading(false);
  };

  const refreshTransferAttendingDoctorStat = (dashboardBarChartListResponse: any): void => {
    setTransferByAttendingDoctor(
      dashboardBarChartListResponse.map(({ name, value }: any) => ({
        id: name,
        label: name,
        value: value,
      }))
    );
    setTransferByAttendingDoctorLoading(false);
  };

  const refreshReasonsForDelayStats = (dashboardReasonsResponse: any): void => {
    setReasonForDelayData(dashboardReasonsResponse.map(({ name, recordCount }: any) => ({ id: name, label: name, value: recordCount })));
    setReasonForDelayLoading(false);
  };

  const refreshCharts = async (reqData: any): Promise<void> => {
    if (!userRoles.IsAdmin && !userRoles.CanViewDashboardAsSMA && authStore.data?.location) reqData.locationId = [authStore.data?.location];

    api
      .getTransferCombinedStats("", reqData)
      .then((res: IDashboardCombinedDTO) => {
        refreshTransferTotalStat(res.model.dashboardResponse);
        refreshTransfersPerformedStats(res.model.dashboardTransferResponse);
        refreshTransportByDayOfAWeekStats(res.model.dashboardListResponse);
        refreshTransferAttendingDoctorStat(res.model.dashboardBarChartListResponse);

        refreshReasonsForDelayStats(res.model.dashboardReasonsResponse);
        refreshTopFiveDestinationsStats(res.model.dashboardTopDestinationsResponse);
        refreshTransportTimesByDayOfAWeekStats(res.model.dashboardTimesByDayOfAWeekStatsResponse);
      })
      .catch(() => {
        setAvgTransferPerDay(0);

        setTransFersPerformedData([]);
        setTransfersPerformedLoading(false);

        setTransportByDayOfWeek([]);

        setTransferByAttendingDoctor([]);
        setTransferByAttendingDoctorLoading(false);

        setReasonForDelayData([]);
        setReasonForDelayLoading(false);

        setTopFiveDestinationsData([]);

        setTransportTimeByDayData([]);
        setTransportByWardLoading(false);
      });

    api
      .getTransportsByWardHospital("", reqData)
      .then((res) => {
        setTransportByWardData(
          res.model.map(({ name, recordCount }: any) => ({
            id: name,
            label: name,
            value: recordCount,
          }))
        );
        setTransportByWardLoading(false);
      })
      .catch(() => {
        setTransportByWardData([]);
        setTransportByWardLoading(false);
      });

    fetchBarChatData(reqData, barChartTabIndex);
  };

  return (
    <>
      <MuiThemeProvider theme={themeDashboard}>
        <Grid container>
          <KPIWidgetPT
            totalTransfers={totalTransfers}
            avgTransferPerDay={avgTransferPerDay}
            transferPerHour={transferPerHour}
            transferDistance={transferDistance}
            percentMultiLoad={percentMultiLoad}
            userRoles={userRoles}
            transferPerDayRedAlert={transferPerDayRedAlert}
            transferPerDayYellowAlert={transferPerDayYellowAlert}
            transferPerHourRedAlert={transferPerHourRedAlert}
            transferPerHourYellowAlert={transferPerHourYellowAlert}
            transferDistanceRedAlert={transferDistanceRedAlert}
            transferDistanceYellowAlert={transferDistanceYellowAlert}
            percentMultiLoadRedAlert={percentMultiLoadRedAlert}
            percentMultiLoadYellowAlert={percentMultiLoadYellowAlert}
          />

          <Grid item xs={12} md={12} lg={12}>
            <div className={classes.transfersPerformed}>
              {transfersPerformedLoading && <LoadingData />}
              {!transfersPerformedLoading && transFersPerformedData.length === 0 && <NoData />}
              {!transfersPerformedLoading && transFersPerformedData.length > 0 && (
                <GroupedBarChart data={transFersPerformedData} title="Transfers Performed" colors={consts.GRAPH_COLORS_BAR} />
              )}
            </div>
          </Grid>

          <Grid item xs={12} md={12} lg={12}>
            <AppBar position="static" className={classes.transfersAppBar}>
              <Tabs
                value={barChartTabIndex}
                onChange={handleBarChartTabChange}
                aria-label="barchart tabs"
                TabIndicatorProps={{
                  style: {
                    display: "none",
                  },
                }}
              >
                <Tab
                  label="Transfers Per Day"
                  {...a11yProps(0)}
                  style={{ flexShrink: 1, textAlign: "left", lineHeight: "unset", fontWeight: barChartTabIndex == 0 ? 600 : "normal" }}
                />
                <Tab
                  label="Transfer Per Hour"
                  {...a11yProps(1)}
                  style={{ flexShrink: 1, textAlign: "left", lineHeight: "unset", fontWeight: barChartTabIndex == 1 ? 600 : "normal" }}
                />
                <Tab
                  label="Transfers Per Location Per Day"
                  {...a11yProps(2)}
                  style={{ flexShrink: 1, textAlign: "left", lineHeight: "unset", fontWeight: barChartTabIndex == 2 ? 600 : "normal" }}
                />
                <Tab
                  label="Transfers Per Location Per Hour"
                  {...a11yProps(3)}
                  style={{ flexShrink: 1, textAlign: "left", lineHeight: "unset", fontWeight: barChartTabIndex == 3 ? 600 : "normal" }}
                />
                <Tab
                  label="Transfer With Pickup Delayed"
                  {...a11yProps(4)}
                  style={{ flexShrink: 1, textAlign: "left", lineHeight: "unset", fontWeight: barChartTabIndex == 4 ? 600 : "normal" }}
                />
                <Tab
                  label="Transfer With Transport Delayed"
                  {...a11yProps(5)}
                  style={{ flexShrink: 1, textAlign: "left", lineHeight: "unset", fontWeight: barChartTabIndex == 5 ? 600 : "normal" }}
                />
                <Tab
                  label="% Multiload"
                  classes={{ wrapper: classes.tabTitle }}
                  {...a11yProps(6)}
                  style={{ flexShrink: 1, textAlign: "left", lineHeight: "unset", fontWeight: barChartTabIndex == 6 ? 600 : "normal" }}
                />
                <Tab
                  label="Transfer Distance (km)"
                  {...a11yProps(7)}
                  style={{ flexShrink: 1, textAlign: "left", lineHeight: "unset", fontWeight: barChartTabIndex == 7 ? 600 : "normal" }}
                />
              </Tabs>
            </AppBar>
            <TabPanel value={barChartTabIndex} index={0}>
              {barChartLoading ? (
                <LoadingData />
              ) : (
                <div>{barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}</div>
              )}
            </TabPanel>
            <TabPanel value={barChartTabIndex} index={1}>
              {barChartLoading ? (
                <LoadingData />
              ) : (
                <div>{barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}</div>
              )}
            </TabPanel>
            <TabPanel value={barChartTabIndex} index={2}>
              {barChartLoading ? (
                <LoadingData />
              ) : (
                <div>{barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}</div>
              )}
            </TabPanel>
            <TabPanel value={barChartTabIndex} index={3}>
              {barChartLoading ? (
                <LoadingData />
              ) : (
                <div>{barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}</div>
              )}
            </TabPanel>
            <TabPanel value={barChartTabIndex} index={4}>
              {barChartLoading ? (
                <LoadingData />
              ) : (
                <div>{barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}</div>
              )}
            </TabPanel>
            <TabPanel value={barChartTabIndex} index={5}>
              {barChartLoading ? (
                <LoadingData />
              ) : (
                <div>{barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}</div>
              )}
            </TabPanel>
            <TabPanel value={barChartTabIndex} index={6}>
              {barChartLoading ? (
                <LoadingData />
              ) : (
                <div>{barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}</div>
              )}
            </TabPanel>
            <TabPanel value={barChartTabIndex} index={7}>
              {barChartLoading ? (
                <LoadingData />
              ) : (
                <div>{barChartData && barChartData.length > 0 ? <BarChartWithTrendLine data={barChartData} /> : <NoData />}</div>
              )}
            </TabPanel>
          </Grid>
          <Grid item xs={12} md={6} lg={6} xl={6}>
            <div className={classes.gridCharts}>
              {reasonForDelayLoading && <LoadingData />}
              {!reasonForDelayLoading && reasonForDelayData.length === 0 && <NoData />}
              {!reasonForDelayLoading && reasonForDelayData.length > 0 && (
                <PieChart chartData={reasonForDelayData} colors={consts.GRAPH_COLORS} title="Reason For Delay" />
              )}
            </div>
          </Grid>
          <Grid item xs={12} md={6} lg={6} xl={6}>
            <div className={classes.gridCharts}>
              {transportByWardLoading && <LoadingData />}
              {!transportByWardLoading && transportByWardData.length === 0 && <NoData />}
              {!transportByWardLoading && transportByWardData.length > 0 && (
                <PieChart
                  chartData={transportByWardData}
                  colors={consts.GRAPH_COLORS}
                  title={`Transport By ${selectedLocations.length == 1 ? "Ward" : "Hospital"}`}
                />
              )}
            </div>
          </Grid>
          <Grid item xs={12} md={6} lg={6} xl={6}>
            <div className={classes.gridCharts}>
              {topFiveDestinationsLoading && <LoadingData />}
              {!topFiveDestinationsLoading && topFiveDestinationsData.length === 0 && <NoData />}
              {!topFiveDestinationsLoading && topFiveDestinationsData.length > 0 && (
                <PieChart chartData={topFiveDestinationsData} colors={consts.GRAPH_COLORS} title="Top 5 Destinations" />
              )}
            </div>
          </Grid>
          <Grid item xs={12} md={6} lg={6} xl={6}>
            <div className={classes.gridCharts}>
              {transportTimeByDayWeekLoading && <LoadingData />}
              {!transportTimeByDayWeekLoading && transportTimeByDayData.length === 0 && <NoData />}
              {!transportTimeByDayWeekLoading && transportTimeByDayData.length > 0 && (
                <BarChart
                  barChartData={transportTimeByDayData}
                  colors={consts.GRAPH_COLORS_BAR2}
                  title="Transport Times By Day of Week"
                  subtitle={!userRoles.IsAdmin && !userRoles.CanViewDashboardAsSMA ? "Actual pickup time" : null}
                />
              )}
            </div>
          </Grid>
          <Grid item xs={12} md={6} lg={6} xl={6}>
            <div className={classes.gridCharts}>
              {transferByAttendingDoctorLoading && <LoadingData />}
              {!transferByAttendingDoctorLoading && transferByAttendingDoctor.length === 0 && <NoData />}
              {!transferByAttendingDoctorLoading && transferByAttendingDoctor.length > 0 && (
                <PieChart chartData={transferByAttendingDoctor} colors={consts.GRAPH_COLORS} title="Attending / Referring Doctor" />
              )}
            </div>
          </Grid>
          <Grid item xs={12} md={6} lg={6} xl={6}>
            <div className={classes.gridCharts}></div>
          </Grid>
        </Grid>
      </MuiThemeProvider>
    </>
  );
};

const useStyles = makeStyles({
  gridCharts: {
    height: "348px",
    border: "1px solid rgba(255, 255, 255, 0.17)",
    borderLeft: "0px",
  },
  transfersAppBar: {
    marginLeft: "3em",
    marginRight: "3em",
    width: "auto",
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  transfersPerformed: {
    height: "348px",
    borderTop: "1px solid rgba(255, 255, 255, 0.17)",
  },
  tabTitle: {
    alignItems: "flex-start",
  },
});

export default PatientTransportDashboardWidget;
